<template>
  <div id="app">
    <h1 v-if="ip_year">{{ $t('Infrastructure plan', {val: parseInt(ip_year)}) }}</h1>
    <p v-if="cashflow_years.start" class="h2">{{ $t('page description', {from_val: parseInt(cashflow_years.start), to_val: parseInt(cashflow_years.end) }) }}</p>
    <div class="callout callout-info">
      <p>{{ $t('callout-note') }}</p>
      <p class="mt-3"><a :href="$t('investment plan website')">{{ $t('investment plan website text') }}</a></p>
    </div>    
    <div class="content-block">
    <fieldset>
      <legend>{{ $t('Budget type') }}</legend>
      <ul class="list-unstyled">
        <li>{{ $t('budget type description 1') }}</li>
        <li>{{ $t('budget type description 2') }}</li>
      </ul>
      <div class="custom-control custom-radio">
        <input type="radio" id="10yearBudget" name="10yearBudget" class="custom-control-input" v-model="budgetType" value="10yearBudget">
        <label class="custom-control-label" for="10yearBudget">{{ $t('10yearBudget') }}</label>	
      </div>
      <div class="custom-control custom-radio">
        <input type="radio" id="6yearBudget" name="6yearBudget" class="custom-control-input" v-model="budgetType" value="6yearBudget">
        <label class="custom-control-label" for="6yearBudget">{{ $t('6yearBudget') }}</label>	
      </div>
     </fieldset>
     <fieldset class="mt-4">
        <legend>{{ $t('Funding type') }}</legend>
        <p>{{ $t('select funding') }}</p>
        <div class="custom-control custom-radio">
          <input type="radio" id="totalInvestmentNeedsRadio" name="totalInvestmentNeedsRadio" class="custom-control-input" v-model="fundingType" value="totalInvestmentNeeds">
          <label class="custom-control-label" for="totalInvestmentNeedsRadio">{{ $t('Total investment needs' + budgetType) }}</label>	
        </div>
        <div class="custom-control custom-radio pl-5">
          <input type="radio" id="fullyFundedRadio" name="fullyFundedRadio" class="custom-control-input" v-model="fundingType" value="fullyFunded">
          <label class="custom-control-label font-weight-normal" for="fullyFundedRadio">{{ $t('Fully funded' + budgetType) }}</label>	
        </div>
        <div class="custom-control custom-radio pl-5">
          <input type="radio" id="partiallyFundedRadio" name="partiallyFundedRadio" class="custom-control-input" v-model="fundingType" value="partiallyFunded">
          <label class="custom-control-label font-weight-normal" for="partiallyFundedRadio">{{ $t('Partially funded' + budgetType) }}</label>
        </div>
        <div class="custom-control custom-radio pl-5">
          <input type="radio" id="fullyPartiallyFundedRadio" name="fullyPartiallyFundedRadio" class="custom-control-input" v-model="fundingType" value="fullyPartiallyFunded">
          <label class="custom-control-label font-weight-normal" for="fullyPartiallyFundedRadio">{{ $t('Fully and partially funded' + budgetType) }}</label>
        </div>
        <div class="custom-control custom-radio pl-5">
          <input type="radio" id="unfundedRadio" name="unfundedRadio" class="custom-control-input" v-model="fundingType" value="unfunded">
          <label class="custom-control-label font-weight-normal" for="unfundedRadio">{{ $t('Unfunded' + budgetType) }}</label>	
        </div>
      </fieldset>    
      <fieldset class="mt-4">
          <legend>{{ $t('View by aggregate') }}</legend>
          <div class="row">
            <div class="col-md-4">
            <select id="aggregate" class="custom-select" v-model="aggregate_by">			
              <option v-for="(group_by) in group_bys" :key="group_by" v-bind:value="group_by">{{ $t('aggregates.' + group_by) }}</option>
            </select>
            </div>
          </div>
          <div v-if="aggregate_by == 'lifecycle_activity_driver'" class="row mt-4">
            <div class="col">
              <p v-html="$t('lifecycle-activity')"></p>
            </div>
          </div>
          <div class="row mt-4">
            <div class="col">
              <a @click="reset" class="badge badge-secondary mr-3">{{ $t("reset") }} <i class="fal fa-times ml-2"></i></a>
            </div>

          </div>
      </fieldset>
     </div>

    <AggregateData 
      v-if="ip_year"
      :wheres="wheres" 
      :aggregate_by="aggregate_by" 
      :filter_for="filter_for"
      :cashflow_years="cashflow_years"
      :budgetType="budgetType"
      :totalInvestmentNeeds="totalInvestmentNeeds"
      v-on:aggregate_update="(val) => aggregate_by = val" 
      v-on:filter_update="(val) => filter_for = val"
      v-on:modal="(record) => modalRecord = record"
      v-on:clear_filter="clearFilter"
    >
    </AggregateData>

    <p class="my-4">
        <a v-if="filter_for" @click="clearFilter" class="badge badge-secondary mr-3">{{ $t('filtering for') }} {{ filter_for }} <i class="fal fa-times ml-2"></i></a>
    </p>    
    
    <nav aria-label="list or map view">
        <div class="nav nav-tabs" id="nav-tab" role="tablist">
          <a class="nav-item nav-link" :class="{'active': listOrMap === 'list' }" id="nav-list-tab" data-toggle="tab" role="tab" aria-controls="nav-list" aria-selected="true" @click="listOrMap = 'list'">{{ $t('List view') }}</a>
          <a class="nav-item nav-link" :class="{'active': listOrMap === 'map'}" id="nav-map-tab" data-toggle="tab" role="tab" aria-controls="nav-map" aria-selected="false" @click="listOrMap = 'map'">{{ $t('Map view') }}</a>
      </div>
    </nav>
    <div class="tab-content" id="nav-tabContent">
      <div class="tab-pane fade" :class="{'active': listOrMap === 'list', 'show': listOrMap === 'list'}" id="nav-list" role="tabpanel" aria-labelledby="nav-list-tab">
        <SearchResults v-if="ip_year" :wheres="wheres" :budgetType="budgetType" v-on:modal="(record) => modalRecord = record"></SearchResults>
      </div>

      <div class="tab-pane fade" :class="{'active': listOrMap === 'map', 'show': listOrMap === 'map'}" id="nav-list" role="tabpanel" aria-labelledby="nav-list-tab">
        <MapResults :wheres="wheres" :budgetType="budgetType" v-if="(listOrMap === 'map') || mapLoaded" v-on:modal="(record) => modalRecord = record"></MapResults>
      </div>
      <DetailModal v-if="modalRecord" :record="modalRecord" v-on:close="modalRecord=null" :cashflow_years="cashflow_years" :wheres="wheres" :budgetType="budgetType"></DetailModal>
    </div>
    <p>{{ $t('originally-published') }}</p>
    <p>{{ $t('date-updated', { date: formattedLastUpdateDate})}}</p>
  </div>
</template>

<script>
import SearchResults from './components/SearchResults.vue';
import AggregateData from './components/AggregateData.vue';
import MapResults from './components/MapResults.vue';
import DetailModal from './components/DetailModal.vue';
import OpenData from './libraries/OpenData';
import axios from 'axios'

export default {
  name: 'App',

  components: { SearchResults, AggregateData, MapResults, DetailModal },

  data() {
    return {
      fundingType: 'totalInvestmentNeeds',
      aggregate_by: 'service_area', //default
      group_bys: ['department','service_area','investment_type','spap_theme','lifecycle_activity_driver','ward'],
      filter_for: null,
      /*
      fullyFunded: true,
      unfunded: false,
      partiallyFunded: false,
      fullyPartiallyFunded: false,
      */
      listOrMap: "list",
      mapLoaded: false,
      modalRecord: null,
      budgetType: "10yearBudget",
      cashflow_years: {
        start: null,
        end: null,
        actual_end: null,
      },
      ip_year: null,
      last_updated_date: null,
    }
  },
  
  async created() {
    const params = {
      select_parameters: ['max(ip_year) as ip_year']
    }
    const ip_year_data = await OpenData.getResults(this.$OPEN_DATA_API,params);
    this.ip_year = ip_year_data[0].ip_year;
    const cashflow_params = { 
      select_parameters: ['max(funding_year) as max_funding_year','min(funding_year) as min_funding_year'],
      where_parameters: [[{column: 'ip_year',type:'=', params: [this.ip_year]}]]
    }
    const data = await OpenData.getResults(this.$OPEN_DATA_API,cashflow_params);
    this.cashflow_years.start = parseInt(data[0].min_funding_year);
    this.cashflow_years.end = parseInt(data[0].max_funding_year);
    this.cashflow_years.actual_end = parseInt(data[0].max_funding_year);

    const meta_data = await this.getMetaData();
    this.last_updated_date = meta_data.dataUpdatedAt;
  },

  watch: {
    listOrMap() {
      if (this.listOrMap === 'map') {
        //don't create and then destroy the map, only load it once
        this.mapLoaded = true;
      }
    },

    budgetType(newValue) {
      if (newValue !== '10yearBudget') {
        //6 years of cashflows for the regular capital budget process
        this.cashflow_years.end = this.cashflow_years.start + 6 - 1;
      } else {
        this.cashflow_years.end = this.cashflow_years.actual_end;
      }
    }

  },

 methods: {
    async getMetaData() {
      const { data } = await axios.get(this.$OPEN_DATA_METADATA);
      return data;
    },
    
    clearFilter() {
      this.filter_for = null;
    },

    reset() {
      this.filter_for = null;
      this.fundingType = 'totalInvestmentNeeds';
      this.budgetType = '10yearBudget'; 
      this.aggregate_by = 'service_area';
      this.listOrMap = 'list';
    }
  },

  computed: {
    formattedLastUpdateDate() {
      if (this.last_updated_date) {
        const d = new Date(this.last_updated_date);
        const options = { month: 'long', day: 'numeric', year: 'numeric' }
        return d.toLocaleDateString(process.env.VUE_APP_I18N_LOCALE + '-CA', options);
      }
      return '';
    },

    unfunded() {
      return (this.fundingType === 'unfunded');
    },
    partiallyFunded() {
      return (this.fundingType === 'partiallyFunded');
    },
    fullyFunded() {
      return (this.fundingType === 'fullyFunded');
    },
    fullyPartiallyFunded() {
      return (this.fundingType === 'fullyPartiallyFunded');
    },
    totalInvestmentNeeds() {
      return (this.fundingType === 'totalInvestmentNeeds');
    },
    wheres() {
      let wheres = [];
      if (this.unfunded || this.partiallyFunded || this.fullyFunded || this.fullyPartiallyFunded) {
        let fundings = [];
        if (this.fullyFunded) {
          fundings.push({column: 'percent_funded', type: '>=', params: [1.0]});
        } else if (this.partiallyFunded) {
          fundings.push({column: 'percent_funded', type: 'BETWEEN', params: [0, 1.0]});
        } else if (this.unfunded) {
          fundings.push({column: 'percent_funded', type: '<', params: [1.0]});
          //fundings.push({column: 'percent_funded', type: '<=', params: [0]});
        } else if (this.fullyPartiallyFunded) {
          fundings.push({column: 'percent_funded', type: '>', params: [0]});
        } else if (this.totalInvestmentNeeds) {
          //no need to do a where, all projects
        }
        wheres.push(fundings);
      }
      if (this.budgetType !== '10yearBudget') {
        wheres.push([
          {
            column: 'funding_year', type: '<=', params: [this.cashflow_years.end],
          }
        ]);
      }
      if (this.filter_for) {
        wheres.push([
          {
            column: this.aggregate_by, type: '=', params: [this.filter_for]
          }
        ])
      }
      if (this.ip_year) {
        wheres.push([
          {
            column: 'ip_year', type: '=', params: [this.ip_year]
          }
        ])
      }
      return wheres;
    },

  },

}
</script>

<style>
    a.badge {
      text-decoration: none !important;
    }
</style>
